/**
 * V54: 좌석 배치도 페이지 (다중 템플릿 시스템)
 *
 * 핵심 변경사항:
 * - 진입 시 항상 운영 템플릿(activeTemplate) 표시
 * - A/B 탭 제거, 편집 버튼 → 템플릿 선택 모달 → 선택한 템플릿 편집
 * - 새 템플릿 생성 시 첫 저장에서 이름 입력 강제
 * - 운영 적용은 activeTemplateId 변경 방식
 *
 * @route /organization/seat-map
 * @access 전직원 (보기), HR/조직장/Admin/임원 (편집)
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Loader, AlertCircle, MapPin, Upload, Copy, Edit3, Check, X, UserX, Hash } from 'lucide-react';

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  useActiveSeatMap,
  useSeatMapTemplateList,
  useSeatMapTemplate,
  useSeatMapTemplateMutations,
} from '../../shared/hooks/useSeatMapTemplates';
import { useCanEditSeatMap } from '../../shared/hooks/useSeatMap';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useAuthContext } from '../../shared/hooks/useAuthContext';

import {
  CompanyCode,
  SeatMapObject,
  SEAT_MAP_DEFAULTS,
  createNewObject,
  SeatMapObjectType,
  generateObjectId,
  formatSeatCode,
  // V55.21: 템플릿 덮어쓰기용 타입
  SeatMapTemplateListItem,
  GetSeatMapTemplateRequest,
  GetSeatMapTemplateResponse,
} from '../../shared/types/seatMap';

import { SeatMapCanvas } from './components/seatMap/SeatMapCanvas';
import { SeatMapToolbar } from './components/seatMap/SeatMapToolbar';
import { SeatMapPalette } from './components/seatMap/SeatMapPalette';
import { EmployeePickerPanel } from './components/seatMap/EmployeePickerPanel';
import { SeatMapPropertiesPanel } from './components/seatMap/SeatMapPropertiesPanel';
import { SeatMapCanvasSettings } from './components/seatMap/SeatMapCanvasSettings';
import { TemplateListModal } from './components/seatMap/TemplateListModal';
import { TemplateNameModal } from './components/seatMap/TemplateNameModal';
import { ModalOverlay } from '../../shared/components/ModalOverlay';

// ============================================
// 디버그 로그 헬퍼
// ============================================

function logView(_tag: string, _data: Record<string, unknown>) {
  // logging removed
}

// ============================================
// 회사 선택 버튼 컴포넌트
// ============================================

interface CompanyCodeButtonProps {
  code: CompanyCode;
  label: string;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

function CompanyCodeButton({
  code,
  label,
  isSelected,
  onClick,
  disabled,
}: CompanyCodeButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 16px',
        border: isSelected ? '2px solid var(--color-primary)' : '1px solid #e2e8f0',
        borderRadius: '8px',
        background: isSelected ? 'var(--color-primary)' : 'white',
        color: isSelected ? 'white' : '#64748b',
        fontSize: '14px',
        fontWeight: isSelected ? '600' : '500',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease',
      }}
    >
      {label}
    </button>
  );
}

// ============================================
// 뷰 모드 타입
// ============================================

type ViewMode = 'view' | 'edit';

// ============================================
// V55.21: 템플릿 이름 편집 컴포넌트
// ============================================

interface EditableTemplateNameProps {
  name: string;
  onChange: (newName: string) => void;
  disabled?: boolean;
}

function EditableTemplateName({ name, onChange, disabled }: EditableTemplateNameProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(name);
  }, [name]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed.length <= 50) {
      onChange(trimmed);
      setIsEditing(false);
    } else if (!trimmed) {
      alert('템플릿 이름을 입력해주세요.');
    } else {
      alert('템플릿 이름은 50자 이하로 입력해주세요.');
    }
  };

  const handleCancel = () => {
    setEditValue(name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '16px' }}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={50}
          style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--color-amber-600)',
            border: '1px solid var(--color-amber-400)',
            borderRadius: '4px',
            padding: '4px 8px',
            width: '200px',
            background: 'var(--color-amber-50)',
          }}
        />
        <button
          onClick={handleSave}
          style={{
            background: 'var(--color-green-500)',
            border: 'none',
            borderRadius: '4px',
            padding: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          title="확인"
        >
          <Check size={14} color="white" />
        </button>
        <button
          onClick={handleCancel}
          style={{
            background: 'var(--color-gray-400)',
            border: 'none',
            borderRadius: '4px',
            padding: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          title="취소"
        >
          <X size={14} color="white" />
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '16px' }}>
      <span style={{ fontSize: '14px', color: 'var(--color-amber-600)', fontWeight: '500' }}>
        편집 중: {name}
      </span>
      {!disabled && (
        <button
          onClick={() => setIsEditing(true)}
          style={{
            background: 'none',
            border: 'none',
            padding: '2px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            opacity: 0.6,
          }}
          title="이름 변경"
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
        >
          <Edit3 size={14} color="var(--color-amber-600)" />
        </button>
      )}
    </div>
  );
}

// ============================================
// V55.21: 템플릿 덮어쓰기 모달
// ============================================

interface TemplateOverwriteModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: SeatMapTemplateListItem[];
  currentTemplateId: string | null;
  currentTemplateName: string;
  isLoading: boolean;
  onSelectSource: (template: SeatMapTemplateListItem) => void;
}

function TemplateOverwriteModal({
  isOpen,
  onClose,
  templates,
  currentTemplateId,
  currentTemplateName,
  isLoading,
  onSelectSource,
}: TemplateOverwriteModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // 현재 편집 중인 템플릿 제외
  const availableTemplates = templates.filter((t) => t.templateId !== currentTemplateId);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const selected = availableTemplates.find((t) => t.templateId === selectedId);
    if (selected) {
      const confirmed = window.confirm(
        `"${currentTemplateName}"의 현재 편집 내용이 "${selected.name}"의 내용으로 교체됩니다.\n\n저장하지 않은 변경사항은 사라집니다. 계속하시겠습니까?`
      );
      if (confirmed) {
        onSelectSource(selected);
        setSelectedId(null);
      }
    }
  };

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          width: '480px',
          maxWidth: '90vw',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-gray-800)', margin: 0 }}>
              다른 템플릿 내용으로 덮어쓰기
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--color-gray-500)', margin: '4px 0 0 0' }}>
              선택한 템플릿의 내용이 현재 편집 화면에 적용됩니다.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} color="var(--color-gray-500)" />
          </button>
        </div>

        {/* V55.22: 목록 영역 - flex: 1 + minHeight + maxHeight로 footer 가시성 보장 */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            minHeight: '180px',
            maxHeight: '300px', // V55.22: 최대 높이 제한으로 footer 영역 확보
          }}
        >
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginLeft: '8px', color: 'var(--color-gray-500)' }}>템플릿 목록 로딩 중...</span>
            </div>
          ) : availableTemplates.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
              덮어쓸 수 있는 다른 템플릿이 없습니다.
            </div>
          ) : (
            <div style={{ padding: '8px' }}>
              {availableTemplates.map((template) => {
                const isSelected = selectedId === template.templateId;
                return (
                  <div
                    key={template.templateId}
                    onClick={() => setSelectedId(template.templateId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      marginBottom: '4px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: isSelected ? 'var(--color-blue-50)' : 'transparent',
                      border: isSelected ? '2px solid var(--color-primary)' : '2px solid transparent',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-gray-50)'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: isSelected ? '2px solid var(--color-primary)' : '2px solid var(--color-gray-300)',
                        background: isSelected ? 'var(--color-primary)' : 'white',
                        marginRight: '12px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isSelected && <Check size={10} color="white" />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-gray-800)' }}>
                          {template.name}
                        </span>
                        {template.isActive && (
                          <span style={{ fontSize: '10px', fontWeight: '600', color: 'white', background: 'var(--color-green-600)', padding: '2px 6px', borderRadius: '8px' }}>
                            운영중
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '2px' }}>
                        {template.objectCount}개 오브젝트
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* V55.22: 버튼 영역 - 가시성 문제 수정
         * 문제 원인:
         * - flexShrink가 없어 리스트가 길 때 footer가 압축됨
         * - CSS 변수 미정의 시 버튼 색상이 투명해질 수 있음
         * - z-index 없이 겹침 발생 가능
         * 해결:
         * - flexShrink: 0으로 압축 방지
         * - 명시적 색상값 사용 (#f59e0b = amber-500)
         * - position: relative + zIndex로 레이어 보장
         */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            flexShrink: 0,
            position: 'relative',
            zIndex: 10,
            paddingTop: '8px',
            borderTop: '1px solid #e2e8f0',
            marginTop: '8px',
            background: 'white',
          }}
        >
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              background: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: '#475569',
            }}
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedId}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px 16px',
              border: 'none',
              borderRadius: '8px',
              // V55.22: 명시적 색상값으로 가시성 보장
              background: selectedId ? '#f59e0b' : '#94a3b8',
              color: '#ffffff',
              cursor: selectedId ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              opacity: 1,
            }}
          >
            <Copy size={16} />
            덮어쓰기
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 메인 페이지 컴포넌트
// ============================================

export function SeatMapPage() {
  // 현재 사용자 정보
  const { user } = useCurrentUser();
  const authContext = useAuthContext();
  const roleIds = authContext?.roleAssignments?.map((ra) => ra.roleId) || [];
  const userCompanyCode = (user?.profile?.companyCode as CompanyCode) || 'KR';

  // 기본 상태
  const [companyCode, setCompanyCode] = useState<CompanyCode>(userCompanyCode);
  const [viewMode, setViewMode] = useState<ViewMode>('view');

  // V55.20: 사용자 프로필 로드 시 companyCode 동기화
  // useState 초기값은 마운트 시 한 번만 평가되므로,
  // 비동기로 로드되는 user?.profile?.companyCode 변경 시 수동 동기화 필요
  // - KR 계정 → KR 템플릿 기본 선택
  // - CN 계정 → CN 템플릿 기본 선택
  const isInitialSyncDone = useRef(false);
  useEffect(() => {
    // 최초 1회만 동기화 (사용자가 수동 변경한 경우 덮어쓰지 않음)
    if (!isInitialSyncDone.current && user?.profile?.companyCode) {
      const profileCompanyCode = user.profile.companyCode as CompanyCode;
      if (profileCompanyCode !== companyCode) {
        logView('companyCode:sync', { from: companyCode, to: profileCompanyCode });
        setCompanyCode(profileCompanyCode);
      }
      isInitialSyncDone.current = true;
    }
  }, [user?.profile?.companyCode, companyCode]);

  // 모달 상태
  const [isTemplateListModalOpen, setIsTemplateListModalOpen] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  // V55.21: 덮어쓰기 모달 상태
  const [isOverwriteModalOpen, setIsOverwriteModalOpen] = useState(false);

  // 편집 중인 템플릿 정보
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingTemplateName, setEditingTemplateName] = useState<string>('');
  const [isNewTemplate, setIsNewTemplate] = useState(false);

  // 편집 상태
  const [editedObjects, setEditedObjects] = useState<SeatMapObject[]>([]);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // 다중 선택 상태
  const [selectedObjectIds, setSelectedObjectIds] = useState<string[]>([]);

  // 복사 버퍼
  const copyBufferRef = useRef<SeatMapObject[]>([]);

  // 캔버스 크기 상태
  const [editedCanvasWidth, setEditedCanvasWidth] = useState<number | null>(null);
  const [editedCanvasHeight, setEditedCanvasHeight] = useState<number | null>(null);
  // V55.21: gridSize 편집 상태 (덮어쓰기 복사 시 필요)
  const [editedGridSize, setEditedGridSize] = useState<number | null>(null);

  // 권한 확인
  const canEdit = useCanEditSeatMap({
    companyCode,
    userRoleIds: roleIds,
    userCompanyCode,
  });

  const canSwitchCompany = roleIds.includes('system_admin');

  // V54: 운영 템플릿 조회 (항상)
  const {
    template: activeTemplate,
    activeTemplateId,
    isLoading: isActiveLoading,
    error: activeError,
    refetch: refetchActive,
  } = useActiveSeatMap({
    companyCode,
    enabled: viewMode === 'view',
  });

  // V54: 템플릿 목록 조회 (모달 열릴 때만)
  // V55.21: 덮어쓰기 모달에서도 목록 필요
  const {
    templates,
    isLoading: isListLoading,
    refetch: refetchList,
  } = useSeatMapTemplateList({
    companyCode,
    enabled: isTemplateListModalOpen || isOverwriteModalOpen,
  });

  // V54: 단일 템플릿 조회 (편집 모드에서)
  const {
    template: editingTemplate,
    isLoading: isTemplateLoading,
    refetch: refetchTemplate,
  } = useSeatMapTemplate({
    companyCode,
    templateId: editingTemplateId,
    enabled: viewMode === 'edit' && !!editingTemplateId,
  });

  // V54: 뮤테이션
  const {
    createTemplate,
    saveTemplate,
    copyTemplate,
    setActiveTemplate,
    deleteTemplate,
    isProcessing,
  } = useSeatMapTemplateMutations(companyCode);

  // V54.1: getActiveSeatMap 결과 상세 로그
  useEffect(() => {
    if (viewMode === 'view' && !isActiveLoading) {
      logView('getActiveSeatMap:result', {
        companyCode,
        activeTemplateId,
        templateExists: !!activeTemplate,
        templateName: activeTemplate?.name ?? null,
        objectsLen: activeTemplate?.objects?.length ?? 0,
      });
    }
  }, [viewMode, isActiveLoading, companyCode, activeTemplateId, activeTemplate]);

  // 상태 변경 로그
  useEffect(() => {
    logView('state', {
      viewMode,
      companyCode,
      isActiveLoading,
      hasActiveTemplate: !!activeTemplate,
      editingTemplateId,
      editingTemplateName,
      isNewTemplate,
    });
  }, [viewMode, companyCode, isActiveLoading, activeTemplate, editingTemplateId, editingTemplateName, isNewTemplate]);

  // 캔버스 설정
  const displayTemplate = viewMode === 'view' ? activeTemplate : editingTemplate;
  const baseCanvasWidth = displayTemplate?.canvasWidth || SEAT_MAP_DEFAULTS.canvasWidth;
  const baseCanvasHeight = displayTemplate?.canvasHeight || SEAT_MAP_DEFAULTS.canvasHeight;
  const baseGridSize = displayTemplate?.gridSize || SEAT_MAP_DEFAULTS.gridSize;
  const canvasWidth = viewMode === 'edit' && editedCanvasWidth !== null ? editedCanvasWidth : baseCanvasWidth;
  const canvasHeight = viewMode === 'edit' && editedCanvasHeight !== null ? editedCanvasHeight : baseCanvasHeight;
  // V55.21: gridSize도 덮어쓰기 복사 시 반영
  const gridSize = viewMode === 'edit' && editedGridSize !== null ? editedGridSize : baseGridSize;

  // 표시할 오브젝트
  const displayObjects = useMemo(() => {
    if (viewMode === 'edit') {
      return editedObjects;
    }
    return activeTemplate?.objects ?? [];
  }, [viewMode, editedObjects, activeTemplate]);

  // 선택된 오브젝트
  const selectedObject = useMemo(() => {
    if (!selectedObjectId) return null;
    return displayObjects.find((obj) => obj.id === selectedObjectId) ?? null;
  }, [selectedObjectId, displayObjects]);

  // 배정된 직원 ID 목록
  const assignedEmployeeIds = useMemo(() => {
    if (!selectedObjectId) return [];
    return displayObjects
      .filter(
        (obj) => obj.type === 'seat' && obj.employeeId && obj.id !== selectedObjectId
      )
      .map((obj) => obj.employeeId as string);
  }, [displayObjects, selectedObjectId]);

  // Zone 목록
  const zones = useMemo(() => {
    return displayObjects.filter((obj) => obj.type === 'zone');
  }, [displayObjects]);

  // 중복 좌석 코드 감지
  const duplicateSeatCodes = useMemo(() => {
    const codeCount = new Map<string, number>();
    const zoneCodeMap = new Map<string, string>();

    displayObjects.forEach((obj) => {
      if (obj.type === 'zone' && obj.zoneCode) {
        zoneCodeMap.set(obj.id, obj.zoneCode);
      }
    });

    displayObjects.forEach((obj) => {
      if (obj.type === 'seat') {
        const zoneCode = obj.zoneId ? zoneCodeMap.get(obj.zoneId) : undefined;
        const seatCode = formatSeatCode(zoneCode, obj.seatNo);
        if (seatCode) {
          codeCount.set(seatCode, (codeCount.get(seatCode) || 0) + 1);
        }
      }
    });

    const duplicates = new Set<string>();
    codeCount.forEach((count, code) => {
      if (count > 1) {
        duplicates.add(code);
      }
    });

    return duplicates;
  }, [displayObjects]);

  // 편집 템플릿 로드 시 오브젝트 초기화
  useEffect(() => {
    if (editingTemplate?.objects) {
      setEditedObjects([...editingTemplate.objects]);
      setEditingTemplateName(editingTemplate.name);
    } else if (editingTemplateId && !isTemplateLoading) {
      setEditedObjects([]);
    }
    setEditedCanvasWidth(null);
    setEditedCanvasHeight(null);
    setEditedGridSize(null); // V55.21: gridSize 초기화
    setHasUnsavedChanges(false);
    setSelectedObjectId(null);
    setSelectedObjectIds([]);
  }, [editingTemplate, editingTemplateId, isTemplateLoading]);

  // ============================================
  // 핸들러 함수들
  // ============================================

  // 템플릿 목록 모달 열기
  const handleOpenTemplateListModal = useCallback(() => {
    setIsTemplateListModalOpen(true);
  }, []);

  // 템플릿 목록 모달 닫기
  const handleCloseTemplateListModal = useCallback(() => {
    setIsTemplateListModalOpen(false);
  }, []);

  // 새 템플릿 생성
  const handleCreateNewTemplate = useCallback(async () => {
    try {
      logView('handleCreateNewTemplate', { action: 'start' });
      const result = await createTemplate({});
      logView('handleCreateNewTemplate', { action: 'created', templateId: result.templateId, name: result.name });
      setEditingTemplateId(result.templateId);
      setEditingTemplateName(result.name);
      setIsNewTemplate(true);
      setEditedObjects([]);
      setIsTemplateListModalOpen(false);
      setViewMode('edit');
    } catch (err: any) {
      logView('handleCreateNewTemplate', { action: 'error', error: err.message });
      alert(`템플릿 생성 실패: ${err.message}`);
    }
  }, [createTemplate]);

  // V54.1: 운영 템플릿 즉시 생성 (EmptyState용)
  // createTemplate -> setActiveTemplate -> refetch 순서로 실행
  const handleCreateActiveTemplate = useCallback(async () => {
    try {
      logView('handleCreateActiveTemplate', { action: 'start', companyCode });

      // 1. 템플릿 생성
      const result = await createTemplate({ name: '운영 좌석 배치도' });
      logView('handleCreateActiveTemplate', { action: 'created', templateId: result.templateId });

      // 2. 운영으로 설정
      await setActiveTemplate(result.templateId);
      logView('handleCreateActiveTemplate', { action: 'setActive', templateId: result.templateId });

      // 3. refetch
      await refetchActive();
      logView('handleCreateActiveTemplate', { action: 'complete' });

      alert('운영 템플릿이 생성되었습니다. 편집 버튼을 눌러 좌석을 배치하세요.');
    } catch (err: any) {
      logView('handleCreateActiveTemplate', { action: 'error', error: err.message });
      alert(`운영 템플릿 생성 실패: ${err.message}`);
    }
  }, [companyCode, createTemplate, setActiveTemplate, refetchActive]);

  // 기존 템플릿 선택하여 편집
  const handleSelectTemplate = useCallback(
    async (templateId: string) => {
      setEditingTemplateId(templateId);
      setIsNewTemplate(false);
      setIsTemplateListModalOpen(false);
      setViewMode('edit');
    },
    []
  );

  // 템플릿 삭제
  const handleDeleteTemplate = useCallback(
    async (templateId: string) => {
      try {
        await deleteTemplate(templateId);
        await refetchList();
      } catch (err: any) {
        alert(`템플릿 삭제 실패: ${err.message}`);
      }
    },
    [deleteTemplate, refetchList]
  );

  // 편집 모드 종료
  const handleExitEditMode = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        '저장하지 않은 변경사항이 있습니다. 편집 모드를 종료하시겠습니까?'
      );
      if (!confirmed) return;
    }
    setViewMode('view');
    setEditingTemplateId(null);
    setEditingTemplateName('');
    setIsNewTemplate(false);
    setSelectedObjectId(null);
    refetchActive();
  }, [hasUnsavedChanges, refetchActive]);

  // V54.1: 저장 (isNewTemplate이면 무조건 이름 입력 모달)
  const handleSave = useCallback(async () => {
    logView('handleSave', { isNewTemplate, editingTemplateId, editingTemplateName });

    // V54.1: isNewTemplate=true면 무조건 이름 입력 모달 표시 (문자열 비교 제거)
    if (isNewTemplate) {
      setIsNameModalOpen(true);
      return;
    }

    // 직접 저장
    if (!editingTemplateId) return;

    try {
      await saveTemplate({
        templateId: editingTemplateId,
        name: editingTemplateName,
        objects: editedObjects,
        canvasWidth,
        canvasHeight,
        gridSize,
      });
      setHasUnsavedChanges(false);
      await refetchTemplate();
      alert('저장되었습니다.');
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  }, [
    isNewTemplate,
    editingTemplateName,
    editingTemplateId,
    editedObjects,
    canvasWidth,
    canvasHeight,
    gridSize,
    saveTemplate,
    refetchTemplate,
  ]);

  // 이름 입력 후 저장
  const handleSaveWithName = useCallback(
    async (name: string) => {
      if (!editingTemplateId) return;

      try {
        await saveTemplate({
          templateId: editingTemplateId,
          name,
          objects: editedObjects,
          canvasWidth,
          canvasHeight,
          gridSize,
        });
        setEditingTemplateName(name);
        setHasUnsavedChanges(false);
        setIsNewTemplate(false);
        setIsNameModalOpen(false);
        await refetchTemplate();
        alert('저장되었습니다.');
      } catch (err: any) {
        alert(`저장 실패: ${err.message}`);
      }
    },
    [editingTemplateId, editedObjects, canvasWidth, canvasHeight, gridSize, saveTemplate, refetchTemplate]
  );

  // 운영으로 적용
  const handleApplyToProduction = useCallback(async () => {
    if (!editingTemplateId) return;

    // 저장되지 않은 변경사항 확인
    if (hasUnsavedChanges) {
      alert('먼저 변경사항을 저장해주세요.');
      return;
    }

    const confirmed = window.confirm(
      `"${editingTemplateName}"을(를) 운영으로 적용하시겠습니까?\n\n전 직원에게 즉시 반영됩니다.`
    );
    if (!confirmed) return;

    try {
      await setActiveTemplate(editingTemplateId);
      alert('운영으로 적용되었습니다.');
      setViewMode('view');
      setEditingTemplateId(null);
      await refetchActive();
    } catch (err: any) {
      alert(`적용 실패: ${err.message}`);
    }
  }, [editingTemplateId, editingTemplateName, hasUnsavedChanges, setActiveTemplate, refetchActive]);

  // V55.21: 템플릿 이름 변경 핸들러
  const handleTemplateNameChange = useCallback((newName: string) => {
    if (newName !== editingTemplateName) {
      setEditingTemplateName(newName);
      setHasUnsavedChanges(true);
      logView('handleTemplateNameChange', { from: editingTemplateName, to: newName });
    }
  }, [editingTemplateName]);

  // V55.21: 덮어쓰기 모달 열기
  const handleOpenOverwriteModal = useCallback(() => {
    setIsOverwriteModalOpen(true);
  }, []);

  // V55.21: 덮어쓰기 모달 닫기
  const handleCloseOverwriteModal = useCallback(() => {
    setIsOverwriteModalOpen(false);
  }, []);

  // V55.21: 다른 템플릿 내용으로 덮어쓰기
  // - 소스 템플릿의 objects, canvasWidth, canvasHeight, gridSize를 로컬 상태에 적용
  // - name은 유지 (현재 편집 중인 템플릿 이름 변경 안 함)
  const handleOverwriteFromTemplate = useCallback(
    async (sourceTemplate: SeatMapTemplateListItem) => {
      try {
        logView('handleOverwriteFromTemplate', {
          sourceTemplateId: sourceTemplate.templateId,
          sourceName: sourceTemplate.name,
          currentTemplateId: editingTemplateId,
        });

        // 소스 템플릿 데이터 조회
        if (!functions) throw new Error('Firebase Functions not initialized');

        const fn = httpsCallable<GetSeatMapTemplateRequest, GetSeatMapTemplateResponse>(
          functions,
          'getSeatMapTemplate'
        );

        const result = await fn({ companyCode, templateId: sourceTemplate.templateId });

        if (!result.data.success || !result.data.template) {
          throw new Error(result.data.error || '템플릿 조회 실패');
        }

        const sourceData = result.data.template;

        // 로컬 상태에 적용 (name은 유지!)
        setEditedObjects([...(sourceData.objects || [])]);
        setEditedCanvasWidth(sourceData.canvasWidth || SEAT_MAP_DEFAULTS.canvasWidth);
        setEditedCanvasHeight(sourceData.canvasHeight || SEAT_MAP_DEFAULTS.canvasHeight);
        setEditedGridSize(sourceData.gridSize || SEAT_MAP_DEFAULTS.gridSize);
        setHasUnsavedChanges(true);
        setSelectedObjectId(null);
        setSelectedObjectIds([]);

        // 모달 닫기
        setIsOverwriteModalOpen(false);

        logView('handleOverwriteFromTemplate:success', {
          objectsCount: sourceData.objects?.length ?? 0,
          canvasWidth: sourceData.canvasWidth,
          canvasHeight: sourceData.canvasHeight,
        });

        alert(`"${sourceTemplate.name}"의 내용으로 덮어쓰기 되었습니다. 저장 버튼을 눌러 저장하세요.`);
      } catch (err: any) {
        logView('handleOverwriteFromTemplate:error', { error: err.message });
        alert(`덮어쓰기 실패: ${err.message}`);
      }
    },
    [companyCode, editingTemplateId]
  );

  // 오브젝트 추가
  const handleAddObject = useCallback((type: SeatMapObjectType) => {
    const newObj = createNewObject(type, 100, 100);
    setEditedObjects((prev) => [...prev, newObj]);
    setSelectedObjectId(newObj.id);
    setHasUnsavedChanges(true);
  }, []);

  // 오브젝트 업데이트
  const handleUpdateObject = useCallback(
    (objectId: string, updates: Partial<SeatMapObject>) => {
      setEditedObjects((prev) =>
        prev.map((obj) => (obj.id === objectId ? { ...obj, ...updates } : obj))
      );
      setHasUnsavedChanges(true);
    },
    []
  );

  // 오브젝트 삭제
  const handleDeleteObject = useCallback((objectId: string) => {
    setEditedObjects((prev) => prev.filter((obj) => obj.id !== objectId));
    setSelectedObjectId(null);
    setSelectedObjectIds([]);
    setHasUnsavedChanges(true);
  }, []);

  // 다중 오브젝트 삭제
  const handleDeleteObjects = useCallback((objectIds: string[]) => {
    if (objectIds.length === 0) return;
    setEditedObjects((prev) => prev.filter((obj) => !objectIds.includes(obj.id)));
    setSelectedObjectId(null);
    setSelectedObjectIds([]);
    setHasUnsavedChanges(true);
  }, []);

  // 다중 오브젝트 업데이트
  const handleUpdateObjects = useCallback(
    (updates: Array<{ id: string; updates: Partial<SeatMapObject> }>) => {
      setEditedObjects((prev) =>
        prev.map((obj) => {
          const update = updates.find((u) => u.id === obj.id);
          return update ? { ...obj, ...update.updates } : obj;
        })
      );
      setHasUnsavedChanges(true);
    },
    []
  );

  // 다중 선택 변경
  const handleSelectionChange = useCallback((objectIds: string[]) => {
    setSelectedObjectIds(objectIds);
    if (objectIds.length === 1) {
      setSelectedObjectId(objectIds[0]);
    } else if (objectIds.length === 0) {
      setSelectedObjectId(null);
    }
  }, []);

  // 복사
  const handleCopy = useCallback(() => {
    if (viewMode !== 'edit' || selectedObjectIds.length === 0) return;
    const objectsToCopy = editedObjects.filter((obj) => selectedObjectIds.includes(obj.id));
    copyBufferRef.current = objectsToCopy;
  }, [viewMode, selectedObjectIds, editedObjects]);

  // 붙여넣기
  const handlePaste = useCallback(() => {
    if (viewMode !== 'edit' || copyBufferRef.current.length === 0) return;

    const OFFSET = 20;
    const newObjects = copyBufferRef.current.map((obj) => {
      const newId = generateObjectId();
      const cleanedObj: SeatMapObject = {
        ...obj,
        id: newId,
        x: obj.x + OFFSET,
        y: obj.y + OFFSET,
      };

      if (obj.type === 'seat') {
        cleanedObj.employeeId = undefined;
        cleanedObj.employeeName = undefined;
        cleanedObj.employeePosition = undefined;
        cleanedObj.seatNo = undefined;
      }

      return cleanedObj;
    });

    setEditedObjects((prev) => [...prev, ...newObjects]);
    setSelectedObjectIds(newObjects.map((o) => o.id));
    setSelectedObjectId(newObjects.length === 1 ? newObjects[0].id : null);
    setHasUnsavedChanges(true);
  }, [viewMode]);

  // 키보드 이벤트
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (viewMode !== 'edit') return;

      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        handleCopy();
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        handlePaste();
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (selectedObjectIds.length > 0) {
          handleDeleteObjects(selectedObjectIds);
        } else if (selectedObjectId) {
          handleDeleteObject(selectedObjectId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, selectedObjectId, selectedObjectIds, handleCopy, handlePaste, handleDeleteObject, handleDeleteObjects]);

  // 오브젝트 선택
  const handleSelectObject = useCallback((objectId: string | null) => {
    setSelectedObjectId(objectId);
  }, []);

  // 캔버스 크기 변경
  const CANVAS_SIZE_STEP = 20;
  const CANVAS_MIN_WIDTH = 400;
  const CANVAS_MIN_HEIGHT = 300;

  const handleCanvasWidthChange = useCallback(
    (delta: number) => {
      const current = editedCanvasWidth ?? baseCanvasWidth;
      const newWidth = Math.max(CANVAS_MIN_WIDTH, current + delta);
      setEditedCanvasWidth(newWidth);
      setHasUnsavedChanges(true);
    },
    [editedCanvasWidth, baseCanvasWidth]
  );

  const handleCanvasHeightChange = useCallback(
    (delta: number) => {
      const current = editedCanvasHeight ?? baseCanvasHeight;
      const newHeight = Math.max(CANVAS_MIN_HEIGHT, current + delta);
      setEditedCanvasHeight(newHeight);
      setHasUnsavedChanges(true);
    },
    [editedCanvasHeight, baseCanvasHeight]
  );

  const handleFitToContents = useCallback(() => {
    if (editedObjects.length === 0) {
      alert('배치된 오브젝트가 없습니다.');
      return;
    }

    const PADDING = 80;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    editedObjects.forEach((obj) => {
      minX = Math.min(minX, obj.x);
      minY = Math.min(minY, obj.y);
      maxX = Math.max(maxX, obj.x + obj.width);
      maxY = Math.max(maxY, obj.y + obj.height);
    });

    let shiftX = 0, shiftY = 0;
    if (minX < 0) shiftX = -minX + PADDING;
    if (minY < 0) shiftY = -minY + PADDING;

    if (shiftX > 0 || shiftY > 0) {
      setEditedObjects((prev) =>
        prev.map((obj) => ({ ...obj, x: obj.x + shiftX, y: obj.y + shiftY }))
      );
      maxX += shiftX;
      maxY += shiftY;
    }

    const newWidth = Math.max(CANVAS_MIN_WIDTH, maxX + PADDING);
    const newHeight = Math.max(CANVAS_MIN_HEIGHT, maxY + PADDING);

    setEditedCanvasWidth(Math.ceil(newWidth / CANVAS_SIZE_STEP) * CANVAS_SIZE_STEP);
    setEditedCanvasHeight(Math.ceil(newHeight / CANVAS_SIZE_STEP) * CANVAS_SIZE_STEP);
    setHasUnsavedChanges(true);
  }, [editedObjects]);

  const handleApplyCanvasSize = useCallback((width: number, height: number) => {
    setEditedCanvasWidth(width);
    setEditedCanvasHeight(height);
    setHasUnsavedChanges(true);
  }, []);

  // 직원 지정
  const handleAssignEmployee = useCallback(
    (employee: { id: string; name: string; position?: string } | null) => {
      if (!selectedObjectId) return;
      handleUpdateObject(selectedObjectId, {
        employeeId: employee?.id,
        employeeName: employee?.name,
        employeePosition: employee?.position,
      });
    },
    [selectedObjectId, handleUpdateObject]
  );

  // V55.24: 좌석 배정 전체 해제
  const handleClearAllSeatAssignments = useCallback(() => {
    // 배정된 좌석 개수 확인
    const assignedSeats = editedObjects.filter(
      (obj) => obj.type === 'seat' && obj.employeeId
    );

    if (assignedSeats.length === 0) {
      alert('배정된 좌석이 없습니다.');
      return;
    }

    // 확인 팝업
    const confirmed = window.confirm(
      `현재 템플릿의 모든 좌석 배정을 해제할까요?\n\n배정된 좌석 ${assignedSeats.length}개의 직원 정보가 제거됩니다.`
    );

    if (!confirmed) return;

    // 모든 seat 오브젝트의 직원 배정 필드 해제
    setEditedObjects((prev) =>
      prev.map((obj) => {
        if (obj.type === 'seat' && obj.employeeId) {
          return {
            ...obj,
            employeeId: undefined,
            employeeName: undefined,
            employeePosition: undefined,
          };
        }
        return obj;
      })
    );

    // 변경사항 플래그 설정
    setHasUnsavedChanges(true);

    // 선택 상태는 유지 (패널 깨짐 방지 - 배정 해제된 좌석으로 UI 갱신됨)
    logView('handleClearAllSeatAssignments', {
      clearedCount: assignedSeats.length,
    });

    alert(`${assignedSeats.length}개 좌석의 배정이 해제되었습니다. 저장 버튼을 눌러 저장하세요.`);
  }, [editedObjects]);

  // V55.25: 좌석 번호 일괄 해제 (V55.26: 디버깅 로그 강화)
  const handleClearAllSeatNumbers = useCallback(() => {
    // 번호가 있는 좌석 개수 확인
    const numberedSeats = editedObjects.filter(
      (obj) => obj.type === 'seat' && obj.seatNo
    );

    if (numberedSeats.length === 0) {
      alert('번호가 지정된 좌석이 없습니다.');
      return;
    }

    // V55.26: 변경 전 상태 로그 (디버깅용)
    logView('handleClearAllSeatNumbers:before', {
      numberedSeatsCount: numberedSeats.length,
      sampleBefore: numberedSeats.slice(0, 3).map((s) => ({
        id: s.id,
        seatNo: s.seatNo,
        employeeId: s.employeeId,
        employeeName: s.employeeName,
      })),
    });

    // 확인 팝업
    const confirmed = window.confirm(
      `현재 템플릿의 모든 좌석 번호를 해제할까요?\n\n좌석 ${numberedSeats.length}개의 번호가 제거됩니다.\n(직원 배정 정보는 유지됩니다.)`
    );

    if (!confirmed) return;

    // 모든 seat 오브젝트의 seatNo 필드만 해제 (employeeId 등은 절대 건드리지 않음)
    setEditedObjects((prev) => {
      const updated = prev.map((obj) => {
        if (obj.type === 'seat' && obj.seatNo) {
          // V55.26: 명시적으로 seatNo만 제거, 다른 필드는 그대로 복사
          const { seatNo: _removed, ...rest } = obj;
          return {
            ...rest,
            seatNo: undefined, // 명시적 undefined 설정
          };
        }
        return obj;
      });

      // V55.26: 변경 후 상태 로그 (디버깅용)
      const updatedSeats = updated.filter((o) => o.type === 'seat');
      logView('handleClearAllSeatNumbers:after', {
        sampleAfter: updatedSeats.slice(0, 3).map((s) => ({
          id: s.id,
          seatNo: s.seatNo,
          employeeId: s.employeeId,
          employeeName: s.employeeName,
        })),
      });

      return updated;
    });

    // 변경사항 플래그 설정
    setHasUnsavedChanges(true);

    // 로그
    logView('handleClearAllSeatNumbers:complete', {
      clearedCount: numberedSeats.length,
    });

    alert(`${numberedSeats.length}개 좌석의 번호가 해제되었습니다. 저장 버튼을 눌러 저장하세요.`);
  }, [editedObjects]);

  const isEditMode = viewMode === 'edit';
  const isLoading = viewMode === 'view' ? isActiveLoading : isTemplateLoading;
  const error = viewMode === 'view' ? activeError : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* 헤더 */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--color-gray-200)', background: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--color-gray-800)', margin: 0 }}>
              좌석 배치도
            </h1>

            {/* V55.21: 회사 선택 (조회 모드: 모두 허용, 편집 모드: 권한 체크) */}
            {/* 버튼 순서는 사용자 companyCode 기준 (KR 사용자: KR→CN, CN 사용자: CN→KR) */}
            {/* 편집 모드에서 타 회사 버튼 비활성화 (system_admin 예외) */}
            <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
              {userCompanyCode === 'CN' ? (
                <>
                  <CompanyCodeButton
                    code="CN"
                    label="대련지사"
                    isSelected={companyCode === 'CN'}
                    onClick={() => setCompanyCode('CN')}
                    disabled={false}
                  />
                  <CompanyCodeButton
                    code="KR"
                    label="한국지사"
                    isSelected={companyCode === 'KR'}
                    onClick={() => setCompanyCode('KR')}
                    disabled={isEditMode && !canSwitchCompany}
                  />
                </>
              ) : (
                <>
                  <CompanyCodeButton
                    code="KR"
                    label="한국지사"
                    isSelected={companyCode === 'KR'}
                    onClick={() => setCompanyCode('KR')}
                    disabled={false}
                  />
                  <CompanyCodeButton
                    code="CN"
                    label="대련지사"
                    isSelected={companyCode === 'CN'}
                    onClick={() => setCompanyCode('CN')}
                    disabled={isEditMode && !canSwitchCompany}
                  />
                </>
              )}
            </div>

            {/* V55.21: 편집 중 템플릿 이름 (편집 가능) */}
            {isEditMode && editingTemplateName && (
              <EditableTemplateName
                name={editingTemplateName}
                onChange={handleTemplateNameChange}
                disabled={isProcessing}
              />
            )}
          </div>

          {/* 툴바 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isEditMode ? (
              <>
                {/* V55.24: 좌석 배정 전체 해제 버튼 */}
                <button
                  onClick={handleClearAllSeatAssignments}
                  disabled={isProcessing}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: '1px solid var(--color-red-300)',
                    borderRadius: '8px',
                    background: 'var(--color-red-50)',
                    color: 'var(--color-red-600)',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.6 : 1,
                  }}
                  title="모든 좌석의 직원 배정 해제"
                >
                  <UserX size={16} />
                  배정 전체 해제
                </button>

                {/* V55.25: 좌석 번호 일괄 해제 버튼 */}
                <button
                  onClick={handleClearAllSeatNumbers}
                  disabled={isProcessing}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: '1px solid var(--color-orange-300)',
                    borderRadius: '8px',
                    background: 'var(--color-orange-50)',
                    color: 'var(--color-orange-600)',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.6 : 1,
                  }}
                  title="모든 좌석의 번호 해제 (배정 정보는 유지)"
                >
                  <Hash size={16} />
                  번호 전체 해제
                </button>

                {/* V55.21: 덮어쓰기 버튼 */}
                <button
                  onClick={handleOpenOverwriteModal}
                  disabled={isProcessing}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: '1px solid var(--color-amber-400)',
                    borderRadius: '8px',
                    background: 'var(--color-amber-50)',
                    color: 'var(--color-amber-700)',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.6 : 1,
                  }}
                  title="다른 템플릿 내용으로 덮어쓰기"
                >
                  <Copy size={16} />
                  덮어쓰기
                </button>

                {/* 운영으로 적용 버튼 */}
                <button
                  onClick={handleApplyToProduction}
                  disabled={isProcessing || hasUnsavedChanges}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '8px',
                    background: hasUnsavedChanges ? 'var(--color-gray-300)' : 'var(--color-green-600)',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isProcessing || hasUnsavedChanges ? 'not-allowed' : 'pointer',
                  }}
                  title={hasUnsavedChanges ? '먼저 저장해주세요' : '운영으로 적용'}
                >
                  <Upload size={16} />
                  운영으로 적용
                </button>

                <SeatMapToolbar
                  viewMode={viewMode}
                  canEdit={canEdit}
                  hasUnsavedChanges={hasUnsavedChanges}
                  isSaving={isProcessing}
                  isCopying={false}
                  onEnterEditMode={handleOpenTemplateListModal}
                  onExitEditMode={handleExitEditMode}
                  onSave={handleSave}
                  onCopyLiveToDraft={() => {}}
                />
              </>
            ) : (
              <SeatMapToolbar
                viewMode={viewMode}
                canEdit={canEdit}
                hasUnsavedChanges={false}
                isSaving={false}
                isCopying={false}
                onEnterEditMode={handleOpenTemplateListModal}
                onExitEditMode={handleExitEditMode}
                onSave={() => {}}
                onCopyLiveToDraft={() => {}}
              />
            )}
          </div>
        </div>

        {/* 캔버스 설정 */}
        {isEditMode && (
          <div style={{ padding: '0 24px 12px' }}>
            <SeatMapCanvasSettings
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              onWidthChange={handleCanvasWidthChange}
              onHeightChange={handleCanvasHeightChange}
              onApplySize={handleApplyCanvasSize}
              onFitToContents={handleFitToContents}
            />
          </div>
        )}
      </div>

      {/* 메인 영역 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* 팔레트 */}
        {isEditMode && (
          <SeatMapPalette
            onAddObject={handleAddObject}
            selectedObjectId={selectedObjectId}
            onDeleteObject={handleDeleteObject}
          />
        )}

        {/* 캔버스 영역 */}
        <div style={{ flex: 1, position: 'relative' }}>
          {error ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', background: 'white', zIndex: 10 }}>
              <AlertCircle size={56} color="var(--color-red-500)" />
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-red-600)', margin: '0 0 8px 0' }}>
                  좌석 배치도를 불러올 수 없습니다
                </p>
                <p style={{ fontSize: '14px', color: 'var(--color-gray-500)', margin: 0, maxWidth: '300px' }}>
                  {error.message}
                </p>
              </div>
              <button
                onClick={() => refetchActive()}
                style={{ marginTop: '8px', padding: '10px 24px', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
              >
                다시 시도
              </button>
            </div>
          ) : isLoading ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', background: 'white', zIndex: 10 }}>
              <Loader size={40} style={{ animation: 'spin 1s linear infinite' }} color="var(--color-primary)" />
              <span style={{ fontSize: '14px', color: 'var(--color-gray-500)' }}>
                좌석 배치도 불러오는 중...
              </span>
            </div>
          ) : !displayTemplate && !isEditMode ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', background: 'white', zIndex: 10 }}>
              <MapPin size={64} color="var(--color-gray-300)" />
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-gray-700)', margin: '0 0 8px 0' }}>
                  운영 좌석 배치도가 없습니다
                </p>
                <p style={{ fontSize: '14px', color: 'var(--color-gray-500)', margin: 0 }}>
                  아직 운영 중인 좌석 배치도가 없습니다.
                </p>
                {canEdit && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    {/* V54.1: 운영 템플릿 즉시 생성 버튼 */}
                    <button
                      onClick={handleCreateActiveTemplate}
                      disabled={isProcessing}
                      style={{
                        padding: '10px 20px',
                        background: 'var(--color-green-600)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.7 : 1,
                      }}
                    >
                      {isProcessing ? '생성 중...' : '운영 템플릿 만들기'}
                    </button>
                    <button
                      onClick={handleOpenTemplateListModal}
                      disabled={isProcessing}
                      style={{
                        padding: '10px 20px',
                        background: 'white',
                        color: 'var(--color-gray-600)',
                        border: '1px solid var(--color-gray-300)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                      }}
                    >
                      템플릿 목록 보기
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : displayObjects.length === 0 && !isEditMode ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', background: 'white', zIndex: 10 }}>
              <MapPin size={64} color="var(--color-gray-300)" />
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-gray-700)', margin: '0 0 8px 0' }}>
                  오브젝트가 없습니다
                </p>
                <p style={{ fontSize: '14px', color: 'var(--color-gray-500)', margin: 0 }}>
                  배치된 좌석/사물이 없습니다.
                </p>
                {canEdit && (
                  <button
                    onClick={handleOpenTemplateListModal}
                    style={{ marginTop: '16px', padding: '10px 20px', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
                  >
                    편집하기
                  </button>
                )}
              </div>
            </div>
          ) : (
            <SeatMapCanvas
              objects={displayObjects}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              gridSize={gridSize}
              isEditMode={isEditMode}
              selectedObjectId={selectedObjectId}
              selectedObjectIds={selectedObjectIds}
              onSelectObject={handleSelectObject}
              onSelectionChange={handleSelectionChange}
              onUpdateObject={handleUpdateObject}
              onUpdateObjects={handleUpdateObjects}
              duplicateSeatCodes={duplicateSeatCodes}
            />
          )}
        </div>

        {/* Zone 속성 패널 */}
        {isEditMode && selectedObject?.type === 'zone' && (
          <SeatMapPropertiesPanel
            selectedObject={selectedObject}
            zones={zones}
            onUpdateObject={handleUpdateObject}
            onClose={() => setSelectedObjectId(null)}
            duplicateSeatCodes={duplicateSeatCodes}
          />
        )}

        {/* V54.7: Meeting Room 속성 패널 */}
        {isEditMode && selectedObject?.type === 'meeting_room' && (
          <SeatMapPropertiesPanel
            selectedObject={selectedObject}
            zones={zones}
            onUpdateObject={handleUpdateObject}
            onClose={() => setSelectedObjectId(null)}
          />
        )}

        {/* V55.22: Floor 속성 패널 */}
        {isEditMode && selectedObject?.type === 'floor' && (
          <SeatMapPropertiesPanel
            selectedObject={selectedObject}
            zones={zones}
            onUpdateObject={handleUpdateObject}
            onClose={() => setSelectedObjectId(null)}
          />
        )}

        {/* V55.22: TextBox 속성 패널 */}
        {isEditMode && selectedObject?.type === 'textBox' && (
          <SeatMapPropertiesPanel
            selectedObject={selectedObject}
            zones={zones}
            onUpdateObject={handleUpdateObject}
            onClose={() => setSelectedObjectId(null)}
          />
        )}

        {/* 좌석 패널 */}
        {isEditMode && selectedObject?.type === 'seat' && (
          <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--color-gray-200)' }}>
            <SeatMapPropertiesPanel
              selectedObject={selectedObject}
              zones={zones}
              onUpdateObject={handleUpdateObject}
              onClose={() => setSelectedObjectId(null)}
              duplicateSeatCodes={duplicateSeatCodes}
            />
            <div style={{ borderTop: '1px solid var(--color-gray-200)' }}>
              <EmployeePickerPanel
                companyCode={companyCode}
                currentEmployeeId={selectedObject.employeeId}
                currentEmployeeName={selectedObject.employeeName}
                assignedEmployeeIds={assignedEmployeeIds}
                onAssign={handleAssignEmployee}
                onClose={() => setSelectedObjectId(null)}
              />
            </div>
          </div>
        )}
      </div>

      {/* 로딩 오버레이 */}
      {isProcessing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} color="var(--color-primary)" />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>처리 중...</span>
          </div>
        </div>
      )}

      {/* V54: 템플릿 선택 모달 */}
      <TemplateListModal
        isOpen={isTemplateListModalOpen}
        onClose={handleCloseTemplateListModal}
        templates={templates}
        isLoading={isListLoading}
        isProcessing={isProcessing}
        onCreateNew={handleCreateNewTemplate}
        onSelectTemplate={handleSelectTemplate}
        onDeleteTemplate={handleDeleteTemplate}
      />

      {/* V54: 이름 입력 모달 */}
      <TemplateNameModal
        isOpen={isNameModalOpen}
        onClose={() => setIsNameModalOpen(false)}
        onSave={handleSaveWithName}
        initialName={editingTemplateName}
        isSaving={isProcessing}
      />

      {/* V55.21: 템플릿 덮어쓰기 모달 */}
      <TemplateOverwriteModal
        isOpen={isOverwriteModalOpen}
        onClose={handleCloseOverwriteModal}
        templates={templates}
        currentTemplateId={editingTemplateId}
        currentTemplateName={editingTemplateName}
        isLoading={isListLoading}
        onSelectSource={handleOverwriteFromTemplate}
      />
    </div>
  );
}
